import React, { useEffect, useRef, useState } from 'react';
import StatBox from '../ui/StatBox';
import { GameIntro, GameOver, GameState, useSaveGameResult } from './GameShell';
import { playCorrectSound, playWrongSound, playSuccessSound, playGameOverSound } from '../../utils/audio';
import { api } from '../../utils/api';
import { User } from '../../types';

const FALLBACK_WORDS = ['cat', 'jump', 'blue', 'happy', 'river', 'planet', 'garden', 'friend', 'yellow', 'pencil', 'window', 'rabbit', 'castle', 'bridge', 'forest', 'basket', 'summer', 'winter', 'school', 'dragon'];
const CORRECT_PER_LEVEL = 3;

type Phase = 'show' | 'recall' | 'result';

const showMsFor = (level: number) => Math.max(650, 2000 - (level - 1) * 160);

const MemoryFlashGame: React.FC<{ user?: User }> = ({ user }) => {
  const [gameState, setGameState] = useState<GameState>('ready');
  const [words, setWords] = useState<string[]>(FALLBACK_WORDS);
  const [phase, setPhase] = useState<Phase>('show');
  const [target, setTarget] = useState('');
  const [answer, setAnswer] = useState('');
  const [wasRight, setWasRight] = useState(false);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [correct, setCorrect] = useState(0);
  const [lives, setLives] = useState(3);
  const [longest, setLongest] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get<string[]>('/api/v1/game/words?count=100')
      .then(list => {
        const clean = list.map(w => w.toLowerCase()).filter(w => /^[a-z]{3,9}$/.test(w));
        if (clean.length > 20) setWords(clean);
      })
      .catch(() => {});
  }, []);

  const pick = (min: number, max: number) => {
    const pool = words.filter(w => w.length >= min && w.length <= max);
    const src = pool.length ? pool : words;
    return src[Math.floor(Math.random() * src.length)];
  };

  const makeTarget = (lvl: number) => {
    if (lvl <= 1) return pick(3, 4);
    if (lvl === 2) return pick(4, 5);
    if (lvl === 3) return pick(5, 6);
    if (lvl === 4) return pick(6, 9);
    if (lvl <= 6) return `${pick(3, 5)} ${pick(3, 5)}`;
    return `${pick(4, 7)} ${pick(4, 7)}`;
  };

  const nextRound = (lvl: number) => {
    setTarget(makeTarget(lvl));
    setAnswer('');
    setPhase('show');
  };

  const startGame = () => {
    setScore(0); setLevel(1); setCorrect(0); setLives(3); setLongest('');
    setGameState('playing');
    nextRound(1);
  };

  useSaveGameResult(user, 'memory-flash', gameState, score);

  // Hide the word after its display time.
  useEffect(() => {
    if (gameState !== 'playing' || phase !== 'show') return;
    const t = setTimeout(() => setPhase('recall'), showMsFor(level));
    return () => clearTimeout(t);
  }, [gameState, phase, target, level]);

  useEffect(() => {
    if (phase === 'recall') inputRef.current?.focus();
  }, [phase]);

  const submit = () => {
    const right = answer.trim().toLowerCase().replace(/\s+/g, ' ') === target;
    setWasRight(right);
    setPhase('result');
    let nextLevel = level;
    let nextLives = lives;
    if (right) {
      const nextCorrect = correct + 1;
      setCorrect(nextCorrect);
      setScore(s => s + target.replace(' ', '').length * 10 * level);
      if (target.length > longest.length) setLongest(target);
      nextLevel = 1 + Math.floor(nextCorrect / CORRECT_PER_LEVEL);
      if (nextLevel > level) { setLevel(nextLevel); playSuccessSound(); } else playCorrectSound();
    } else {
      nextLives = lives - 1;
      setLives(nextLives);
      playWrongSound();
    }
    setTimeout(() => {
      if (nextLives <= 0) { playGameOverSound(); setGameState('over'); }
      else nextRound(nextLevel);
    }, right ? 900 : 1700);
  };

  if (gameState === 'ready') {
    return (
      <GameIntro icon="🧠" title="Memory Flash" skill="eyes-on-screen typing -- read once, then type without peeking." accent="text-sky-600" button="bg-gradient-to-r from-sky-500 to-indigo-500" onStart={startGame}>
        A word flashes on the screen for a moment, then disappears. Type it from memory and press Enter. Every 3 correct answers the flash gets quicker and the words get longer -- later you'll remember two words at once! You have 3 lives.
      </GameIntro>
    );
  }
  if (gameState === 'over') {
    return (
      <GameOver
        message={level >= 5 ? 'What a memory!' : 'Good focus -- try to beat your level!'}
        stars={level >= 6 ? 3 : level >= 4 ? 2 : correct > 0 ? 1 : 0}
        stats={[{ label: 'Score', value: score }, { label: 'Level', value: level }, { label: 'Remembered', value: correct }]}
        button="bg-gradient-to-r from-sky-500 to-indigo-500"
        onRestart={startGame}
      >
        {longest && <p className="mt-3 text-gray-600">Longest remembered: <span className="font-mono font-bold text-sky-700">{longest}</span></p>}
      </GameOver>
    );
  }

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-around items-center mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
        <StatBox label="Score" value={score} />
        <StatBox label="Level" value={level} />
        <StatBox label="Remembered" value={correct} />
        <div className="flex flex-col items-center px-4 py-2 min-w-[80px] rounded-2xl bg-white shadow-md ring-2 ring-white/70">
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-rose-500">Lives</span>
          <span className="text-2xl" aria-label={`${lives} lives left`}>{'❤️'.repeat(lives)}<span className="opacity-20">{'❤️'.repeat(3 - lives)}</span></span>
        </div>
      </div>
      <div className="relative rounded-2xl border-4 border-white shadow-inner bg-gradient-to-br from-sky-200 via-indigo-100 to-pink-100 flex flex-col items-center justify-center select-none" style={{ minHeight: 340 }}>
        {phase === 'show' && (
          <>
            <div key={target} className="mf-flash absolute inset-0 rounded-xl bg-white pointer-events-none" />
            <div className="relative px-10 py-8 rounded-2xl bg-white border-2 border-sky-300 shadow-xl">
              <span className="font-mono text-5xl md:text-6xl font-bold text-gray-800 tracking-wide">{target}</span>
            </div>
            <div className="relative mt-6 w-64 h-2 rounded-full bg-sky-200 overflow-hidden">
              <div key={`bar-${target}`} className="h-full bg-sky-500 mf-bar" style={{ animationDuration: `${showMsFor(level)}ms` }} />
            </div>
            <p className="relative mt-3 text-sky-700 font-semibold">Remember it!</p>
          </>
        )}
        {phase === 'recall' && (
          <>
            <div className="text-6xl mb-4" aria-hidden="true">🙈</div>
            <p className="text-gray-600 font-semibold mb-4">What was it? Type it and press Enter.</p>
            <input
              ref={inputRef}
              value={answer}
              onChange={e => setAnswer(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && answer.trim()) { e.preventDefault(); submit(); } }}
              className="w-72 max-w-[90%] p-3 text-center text-2xl font-mono border-2 border-sky-300 rounded-xl focus:border-sky-500 focus:ring-sky-500"
              autoComplete="off" autoCorrect="off" spellCheck={false} aria-label="Type the word you remember"
            />
          </>
        )}
        {phase === 'result' && (
          <div className="text-center">
            <div className="text-6xl mb-3" aria-hidden="true">{wasRight ? '🎉' : '😮'}</div>
            {wasRight
              ? <p className="text-2xl font-bold text-green-600">Correct!</p>
              : <>
                  <p className="text-xl font-bold text-red-500">Not quite.</p>
                  <p className="mt-2 text-gray-600">It was <span className="font-mono font-bold text-gray-800">{target}</span></p>
                </>}
          </div>
        )}
        <style>{`
          @keyframes mf-flash { from { opacity: 0.9; } to { opacity: 0; } }
          .mf-flash { animation: mf-flash 0.35s ease-out forwards; }
          @keyframes mf-bar { from { width: 100%; } to { width: 0%; } }
          .mf-bar { animation-name: mf-bar; animation-timing-function: linear; animation-fill-mode: forwards; }
        `}</style>
      </div>
    </div>
  );
};

export default MemoryFlashGame;
